/**
 * `ai-explanation` (12) data layer — the ONLY place this feature talks to the server. Every path +
 * capability below is verified against server/src/modules/ai-explanation/{routes,controller}.js. Server
 * data lives only in TanStack Query (lab-scoped keys); the generate/regenerate mutations invalidate the
 * read key so the panel re-renders the freshly persisted row.
 *
 * Endpoints consumed (all under /api/v1, CLOUD-only — not mounted on a local install):
 *   GET   /ai/explications/:crId            ai.explication.read       → Explication | null
 *   POST  /ai/explications/:crId/generer    ai.explication.generate   → GenerationResult (never throws)
 *   POST  /ai/explications/:crId/regenerer  ai.explication.regenerate → GenerationResult (never throws)
 *
 * CLOUD-ONLY DEGRADE. On a local install the routes are absent → the read GET 404s (ApiError NOT_FOUND
 * / httpStatus 404). `isModuleUnmounted(err)` lets the screen render the clean "not available on this
 * install" state instead of a generic error. On a cloud install the read NEVER 404s for an existing CR
 * (the controller returns `ok(null)`), so a 404 is an unambiguous "module not deployed" signal.
 */
import { ApiError, api, qk, useApiMutation, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import type { QueryKey } from "@tanstack/react-query";
import type { Explication, GenerationResult, Id } from "./types";

export { AIX_CAPS, MODULE_CAPS } from "./caps";

/** Lab id for query scoping — from the live auth session (multi-tenant cache isolation). */
export function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
}

/** Lab-scoped query-key sub-factory for the ai-explanation feature. */
export const aixKeys = {
  explication: (labId: string | null, crId: string): QueryKey =>
    qk.scoped(labId, "ai-explanation", "explication", crId),
};

/**
 * True when the error means the ai-explanation module is NOT mounted on this install (cloud-only,
 * absent on a local box). A bare 404 / NOT_FOUND on the read probe is the unambiguous signal: on a
 * cloud install the controller never 404s an existing CR (it returns `ok(null)`).
 */
export function isModuleUnmounted(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    !err.isForbidden &&
    (err.httpStatus === 404 || err.code === "NOT_FOUND" || err.code === "NOT_IMPLEMENTED")
  );
}

/**
 * GET /ai/explications/:crId — the persisted explanation alongside a signed report. Returns the latest
 * non-`regenere` row, or `null` (graceful absence — no explanation generated yet). The query is enabled
 * only when a crId is present, the caller is online, and the user may read.
 */
export function useExplication(crId: string | null, enabled: boolean) {
  const labId = useLabId();
  return useApiQuery<Explication | null>(
    aixKeys.explication(labId, crId ?? "_"),
    `/ai/explications/${crId ?? "_"}`,
    { enabled: enabled && !!crId },
  );
}

/**
 * POST /ai/explications/:crId/generer — generate the bilingual explanation. The server NEVER throws:
 * it returns a tagged `{ status:'ok'|'disabled'|'error', texte, disclaimer, modele }`. We surface that
 * tag to the panel so a missing key / quota / outage degrades to the "indisponible" state, never an
 * error toast. On `ok` we invalidate the read key so the panel re-reads the persisted row.
 */
export function useGenerate(crId: string | null) {
  const labId = useLabId();
  return useApiMutation<GenerationResult, void>(
    () => api.post<GenerationResult>(`/ai/explications/${crId ?? "_"}/generer`),
    { invalidate: [aixKeys.explication(labId, crId ?? "_")] },
  );
}

/**
 * POST /ai/explications/:crId/regenerer — regenerate (closes the current row → regenere + writes fresh,
 * the signed report stays born-immutable). Same never-throws tagged result + read-key invalidation.
 */
export function useRegenerate(crId: string | null) {
  const labId = useLabId();
  return useApiMutation<GenerationResult, void>(
    () => api.post<GenerationResult>(`/ai/explications/${crId ?? "_"}/regenerer`),
    { invalidate: [aixKeys.explication(labId, crId ?? "_")] },
  );
}

/** Stringify a wire id for keys / params (BigInt serializes to string). */
export function idStr(id: Id | null | undefined): string {
  return id == null ? "" : String(id);
}
